import { TooltipProvider } from '@monksflow/monks-ui';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { ZoomButton } from './ZoomButton';
import { useTranslation } from 'react-i18next';

interface Props {
  zoom: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
  /**
   * Retained so callers can keep threading reset state through; the
   * "Fit to screen" button is hidden for now to avoid visual collision
   * with the fullscreen toggle in HoverControlsPill. Re-render the
   * button when a distinct icon/placement is decided.
   */
  canReset: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
}

/**
 * Zoom controls (out / % / in). Renders bare — the caller owns the
 * surrounding `ControlGroup` pill, so zoom can either stand alone or be
 * composed into a shared pill (e.g. merged with the PDF page navigator).
 */
export function ZoomControls({ zoom, canZoomIn, canZoomOut, onZoomIn, onZoomOut }: Props) {
  const { t } = useTranslation('projects');

  const percent = Math.round(zoom * 100);
  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-2 px-6 py-2">
        <ZoomButton
          label={t('projectDetail.zoomControls.zoomOut')}
          Icon={MinusIcon}
          disabled={!canZoomOut}
          onClick={onZoomOut}
        />
        <span className="min-w-12 text-center text-sm font-medium tabular-nums" aria-live="polite">
          {percent}%
        </span>
        <ZoomButton
          label={t('projectDetail.zoomControls.zoomIn')}
          Icon={PlusIcon}
          disabled={!canZoomIn}
          onClick={onZoomIn}
        />
      </div>
    </TooltipProvider>
  );
}
